<script setup lang="ts">
import {reactive, ref, useTemplateRef, watch, watchEffect} from "vue";
import {useRoute, useRouter} from "vue-router";
import {breakpointsTailwind, useBreakpoints, watchDebounced} from "@vueuse/core";
import type {DataTableFilterEvent, DataTableFilterMeta} from "primevue/datatable";
import DataTable from "primevue/datatable";
import {Button, Icon, IconField, InputIcon, InputText, Popover, ToggleSwitch} from "@/Components/Library";
import Label from "@/Components/Library/Label.vue";

type Props = {
    data: {
        data: Array<any>;
        links: {
            first: string;
            last: string;
            prev: string | null;
            next: string | null;
        };
        meta: {
            current_page: number;
            from: number;
            last_page: number;
            links: Array<any>;
            path: string;
            per_page: number;
            to: number;
            total: number;
        };
    };
    // filters?: DataTableFilters;
    filters?: any;
    filterDisplay?: "menu" | "row";
    allowTagSearch?: boolean;
    loading: boolean;
    showSearch?: boolean;
    allowExport?: boolean;
    defaultSort?: string;
    rowClass?: Function | string | null;
};

const currentRoute = useRoute();
const router = useRouter();
const breakpoints = useBreakpoints(breakpointsTailwind);
const props = withDefaults(defineProps<Props>(), {
    data: () => ({
        data: [],
        links: {
            first: "",
            last: "",
            prev: null,
            next: null,
        },
        meta: {
            current_page: 1,
            from: 1,
            last_page: 1,
            links: [],
            path: "",
            per_page: 1,
            to: 0,
            total: 0,
        },
    }),
    filterDisplay: "menu",
    showSearch: true,
    allowExport: true,
    allowTagSearch: false,
    defaultSort: "-created_at",
    rowClass: null,
});

const emit = defineEmits<{
    (e: "page", page: number): void;
    (e: "sort", sort: any): void;
    (e: "filter", filter: any): void;
}>();

const paginatorTemplates = {
    sm: "PrevPageLink CurrentPageReport NextPageLink",
    md: "RowsPerPageDropdown PrevPageLink CurrentPageReport NextPageLink",
    lg: "RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink",
    default: "RowsPerPageDropdown CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink",
};

const dataTableState = reactive({
    data: [],
    links: {
        first: "",
        last: "",
        prev: null,
        next: null,
    },
    meta: {
        current_page: 1,
        from: 1,
        last_page: 1,
        links: [],
        path: "",
        per_page: 1,
        to: 0,
        total: 0,
    },
});

const params = reactive<{
    page: {
        number: number;
        size: number;
    };
    sort: string | null;
    filter: Record<string, any>;
}>({
    page: {
        number: currentRoute.query.page?.number ?? 1,
        size: currentRoute.query.page?.size ?? 10,
    },
    sort: (() => {
        const sortQuery = currentRoute.query.sort?.[0];
        return !sortQuery?.trim() || sortQuery === "-" ? (props.defaultSort as string | null) : sortQuery;
    })(),
    filter: {
        ...currentRoute.query.filter,
        search: undefined,
    },
});
const search = ref(currentRoute.query?.filter?.search ?? null);
const tagSearch = reactive({
    tags: [],
    strict: false,
});

const sortField = ref(params.sort ? params.sort.replace(/^-/, "") : null);
const sortOrder = ref(params.sort ? (params.sort.startsWith("-") ? -1 : 1) : null);
const tagsPanel = ref<Popover>();
const paginatorTemplate = ref(paginatorTemplates.default);
const dt = useTemplateRef("dt");
const expandedRows = ref([]);
const filters = ref(props.filters ? {...props.filters, search: {value: null}} : {search: {value: null}});

const syncFiltersValues = () => {
    if (filters.value) {
        Object.entries(filters.value).forEach(([key, filter]) => {
            if (params.filter[key]) {
                filter.value = params.filter[key];
            }

            if (filter.constraints) {
                Object.entries(filter.constraints).forEach(([i, constraint]) => {
                    if (params.filter[key][i]) {
                        constraint.value = params.filter[key][i].value;
                        constraint.matchMode = params.filter[key][i].operator;
                    }
                });
            }
        });
    }
};
syncFiltersValues();

const exportCSV = () => {
    dt.value.exportCSV();
};

const resetFilters = () => {
    params.filter = {
        search: null,
    };
    filters.value = {
        search: {
            value: null,
        },
    };
};

const onPage = (e: any) => {
    expandedRows.value = [];
    params.page.number = e.page + 1;
    params.page.size = e.rows;
};

const onSort = (e: any) => {
    if (e.sortField === null) {
        params.sort = null;
    } else {
        params.sort = [e.sortOrder > 0 ? e.sortField : `-${e.sortField}`];
    }
};
const onFilter = (e: DataTableFilterEvent) => {
    params.filter = {};
    if (filters.value) {
        Object.entries(filters.value).forEach(([key, filter]) => {
            if (filter.value !== null) {
                params.filter[key] = filter.value;
            }

            if (filter.constraints) {
                params.filter[key] = {};

                filter.constraints.forEach((constraint, i) => {
                    if (constraint.value !== null) {
                        params.filter[key][i] = {
                            operator: constraint.matchMode,
                            value: constraint.value,
                        };
                    }
                });
            }
        });
    }
    params.page.number = 1;
};

const setPaginatorTemplate = () => {
    if (breakpoints.smallerOrEqual("sm").value) {
        paginatorTemplate.value = paginatorTemplates.sm;
    } else if (breakpoints.smallerOrEqual("md").value) {
        paginatorTemplate.value = paginatorTemplates.md;
    } else if (breakpoints.smallerOrEqual("lg").value) {
        paginatorTemplate.value = paginatorTemplates.lg;
    } else {
        paginatorTemplate.value = paginatorTemplates.default;
    }
};

const replaceQuery = async () => {
    await router.replace({
        name: currentRoute.name,
        query: {
            page: {
                number: params.page.number,
                size: params.page.size,
            },
            sort: params.sort,
            filter: {
                ...params.filter,
                search: search.value,
                tag_search: tagSearch.tags.length
                    ? {
                          tags: tagSearch.tags.map((t) => t.name),
                          strict: tagSearch.strict,
                      }
                    : undefined,
            },
        },
    });

    emit("filter", {
        ...params.filter,
        search: search.value,
    });
};

watchEffect(() => Object.assign(dataTableState, props.data));

watch(
    [params, tagSearch],
    async (newParams) => {
        if (search.value && search.value !== currentRoute.query.filter?.search) {
            return;
        }

        await replaceQuery();
    },
    {deep: true},
);

watchDebounced(search, replaceQuery, {debounce: 500, deep: true});

setPaginatorTemplate();

const rowClass = (data) =>
    !props.rowClass && data.deleted_at
        ? {
              "dark:even:!bg-red-600/10 dark:odd:!bg-red-600/10 even:!bg-red-100 odd:!bg-red-100": data.deleted_at,
          }
        : props.rowClass;
</script>
<template>
    <DataTable
        size="small"
        stripedRows
        ref="dt"
        v-bind="$attrs"
        lazy
        :value="dataTableState.data ?? []"
        v-model:filters="filters as DataTableFilterMeta"
        :filter-display="filterDisplay"
        :first="(+params.page.number - 1) * +params.page.size"
        paginator
        :rows="+params.page.size"
        :rowsPerPageOptions="[5, 10, 20, 50, data.meta?.total > 50 ? data.meta.total : 100]"
        removableSort
        :totalRecords="+data.meta?.total"
        :loading="loading"
        v-model:expandedRows="expandedRows"
        @page="onPage($event)"
        @sort="onSort($event)"
        @filter="onFilter($event)"
        dataKey="id"
        :paginatorTemplate="paginatorTemplate"
        currentPageReportTemplate="{first} to {last} of {totalRecords}"
        :sortField="sortField"
        :sortOrder="sortOrder"
        :rowClass="rowClass"
    >
        <template #header v-if="showSearch">
            <slot name="header">
                <slot name="before-header"></slot>
                <div class="m-container flex items-center">
                    <Button
                        v-if="allowExport"
                        class="mr-2 bg-askr-green text-white hover:brightness-110"
                        :icon="{
                            icon: 'material-symbols:download',
                        }"
                        @click="exportCSV"
                    >
                        <span class="hidden lg:inline">Export</span>
                    </Button>
                    <IconField class="mr-2 grow">
                        <InputIcon>
                            <Icon icon="prime:search" />
                        </InputIcon>
                        <InputText
                            id="search"
                            type="search"
                            v-model="search"
                            @update:modelValue="onFilter"
                            placeholder="Keyword Search"
                            class="py-2 pl-10"
                            fluid
                        />
                        <InputIcon class="prime:times-circle" @click="search = ''" />
                    </IconField>
                    <!-- TODO: Fix reset button -->
                    <!-- TODO: Add tag search -->
                    <div class="mr-2" v-if="allowTagSearch">
                        <Button
                            :icon="{
                                icon: 'prime:tags',
                            }"
                            class="bg-orange-500 text-white hover:brightness-110"
                            :outlined="!tagSearch.tags.length"
                            @click="tagsPanel?.toggle"
                        >
                            <span class="hidden lg:inline">Tags</span>
                        </Button>
                        <Popover ref="tagsPanel">
                            <div class="w-96">
                                <div class="flex items-start">
                                    <div class="mb-2 flex items-center gap-2 py-2">
                                        <Label for="strict">Strict mode</Label>
                                        <ToggleSwitch input-id="strict" v-model="tagSearch.strict" />
                                    </div>
                                    <Button
                                        class="ml-auto text-xs"
                                        link
                                        @click="
                                            tagSearch.tags = [];
                                            tagSearch.strict = false;
                                        "
                                    >
                                        Clear
                                    </Button>
                                </div>
                                <!--                                <TagSelect v-model="tagSearch.tags" :allowCreating="false" />-->
                            </div>
                        </Popover>
                    </div>
                    <Button
                        class="bg-red-500 text-white hover:brightness-110"
                        :icon="{
                            icon: 'mdi:filter-off-outline',
                        }"
                        @click="resetFilters()"
                    >
                        <span class="hidden lg:inline">Clear</span>
                    </Button>
                </div>
                <slot name="after-header"></slot>
            </slot>
        </template>
        <slot></slot>
        <template #expansion="slotProps">
            <slot name="expansion" v-bind="slotProps">
                {{ slotProps }}
            </slot>
        </template>
    </DataTable>
</template>
<style>
table .p-button.p-button-icon-only.p-button-rounded {
    width: 2rem;
    height: 2rem;
}
</style>
